export const HELP_FILE_LIMITS = {
  markdownBytes: 2 * 1024 * 1024,
  filenameLength: 120,
} as const;

export const HELP_MEDIA_TYPES = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".mp4": "video/mp4",
} as const;

const MARKDOWN_MEDIA_TYPES = new Set(["", "text/markdown", "text/plain"]);
const SAFE_FILENAME = /^[A-Za-z0-9][A-Za-z0-9._ -]*$/;

export type HelpBundleValidation = {
  errors: string[];
  markdownFile: File | null;
};

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
};

export const canonicalHelpMediaType = (filename: string): string | null => {
  const extension = extensionOf(filename);
  if (extension === ".md") return "text/markdown";
  return HELP_MEDIA_TYPES[extension as keyof typeof HELP_MEDIA_TYPES] ?? null;
};

const megabytes = (bytes: number): number => bytes / 1024 / 1024;

export const isSafeHelpFilename = (filename: string): boolean =>
  filename.length <= HELP_FILE_LIMITS.filenameLength &&
  SAFE_FILENAME.test(filename) &&
  !filename.includes("..") &&
  !filename.includes("/") &&
  !filename.includes("\\");

export const validateHelpBundle = (
  incomingFiles: Iterable<File>,
): HelpBundleValidation => {
  const files = Array.from(incomingFiles);
  const errors: string[] = [];
  const markdownCandidates = files.filter(
    ({ name }) => extensionOf(name) === ".md",
  );
  const names = new Set<string>();

  if (markdownCandidates.length !== 1) {
    errors.push("Add exactly one Markdown (.md) file.");
  }

  for (const file of files) {
    const normalizedName = file.name.toLowerCase();
    const extension = extensionOf(file.name);

    if (!isSafeHelpFilename(file.name)) {
      errors.push(
        `Filenames cannot include folders or special path characters: ${file.name}`,
      );
    }

    if (names.has(normalizedName)) {
      errors.push(`Duplicate filenames are not allowed: ${file.name}`);
    }
    names.add(normalizedName);

    if (extension === ".md") {
      if (!MARKDOWN_MEDIA_TYPES.has(file.type)) {
        errors.push(`Unsupported file type: ${file.name}`);
      }
      if (file.size > HELP_FILE_LIMITS.markdownBytes) {
        errors.push(
          `${file.name} is larger than ${megabytes(
            HELP_FILE_LIMITS.markdownBytes,
          )} MB.`,
        );
      }
      continue;
    }

    errors.push(`Only Markdown (.md) files can be uploaded: ${file.name}`);
  }

  return {
    errors: Array.from(new Set(errors)),
    markdownFile:
      markdownCandidates.length === 1 ? markdownCandidates[0] : null,
  };
};
